import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AlertTriangle, LogIn, ShoppingCart } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import { getTenantSlug } from "../../lib/tenant";
import { useCart } from "../../lib/cart";
import CustomerHeader from "../../components/CustomerHeader";
import ProductCard from "../../components/ProductCard";
import EmptyState, { LoadingState } from "../../components/EmptyState";

export default function Products() {
  const { user, tenant } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const { count } = useCart();
  const [search, setSearch] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  // Starts true so the first render shows a loading state instead of the
  // "No products in this category" empty message — before this, `products`
  // and `cats` both started as [] so the empty-state briefly flashed on
  // every visit to the Shop tab until the fetch below resolved.
  const [loading, setLoading] = useState(true);
  // A failed fetch used to be indistinguishable from an empty catalogue:
  // every error fell through to "No products in this category", so a 403 /
  // 404 / 500 looked to the customer (and to support) like the shop was
  // simply empty. Track it explicitly and say so.
  const [loadError, setLoadError] = useState(null);
  const slug = getTenantSlug();
  const activeCat = search.get("cat") || "all";
  const activeSub = search.get("sub") || "all";
  const activeBrand = search.get("brand") || "all";

  // Load categories + all products once (we filter client-side so the chip
  // bar updates instantly without a fetch per click).
  //
  // A signed-in customer reads everything from ONE authenticated call, the
  // same way CustomerHome does. Two reasons:
  //
  //  1. It drops the tenant-SLUG dependency. Products came from the
  //     customer's own `tenant_id` while categories and brands came from
  //     whatever `getTenantSlug()` resolved to. When those disagreed the
  //     screen went blank — every category chip filtered against products
  //     from a different tenant, matching nothing — and if the slug didn't
  //     resolve at all the public calls 404'd.
  //  2. It removes the `Promise.all` all-or-nothing coupling: a single
  //     failing request rejected the whole batch, so NONE of the three
  //     setState calls ran and products, categories and brands were all
  //     left empty — rendering as "No products in this category" with no
  //     error anywhere. That is the exact blank-Shop symptom.
  //
  // Signed-out browsing still needs the public slug endpoints (there's no
  // session to derive a tenant from), but they're settled independently so
  // one failure can't blank the others.
  const loadCatalogue = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (user) {
        const { data } = await api.get("/api/customer/home");
        setProducts(data.products || []);
        setCats(data.categories || []);
        setBrands(data.brands || []);
        return;
      }
      const [pRes, cRes, hRes] = await Promise.allSettled([
        api.get(`/api/public/tenant/${slug}/products`),
        api.get(`/api/public/tenant/${slug}/categories`),
        api.get(`/api/public/tenant/${slug}/home`),  // for brands
      ]);
      if (pRes.status === "rejected") throw pRes.reason;
      setProducts(pRes.value.data.products || []);
      // Chips are a refinement, not the point of the page — losing them
      // must not cost the customer the product grid.
      setCats(cRes.status === "fulfilled" ? (cRes.value.data.categories || []) : []);
      setBrands(hRes.status === "fulfilled" ? (hRes.value.data.brands || []) : []);
    } catch (e) {
      setLoadError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCatalogue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, slug]);

  // Top-level categories only — subcategories get their own chip row (see
  // subcats below), not mixed into the main bar. For a tenant with no
  // subcategory hierarchy at all, every category has parent_id == null,
  // so this is identical to `cats` — no behavior change for them.
  const topCats = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);
  const activeCatObj = activeCat !== "all" ? cats.find((c) => c.slug === activeCat) : null;
  const subcats = activeCatObj ? cats.filter((c) => c.parent_id === activeCatObj.id) : [];
  const activeSubObj = subcats.length ? subcats.find((c) => c.slug === activeSub) : null;

  const filtered = useMemo(() => {
    let out = products;
    if (activeCatObj) {
      if (subcats.length === 0) {
        // No subcategories — exactly the original single-level filter.
        out = out.filter((p) => p.category_id === activeCatObj.id || p.category === activeCatObj.slug);
      } else if (activeSubObj) {
        // A specific subcategory is selected — only its products.
        out = out.filter((p) => p.category_id === activeSubObj.id);
      } else {
        // Parent category selected, no specific subcategory yet — show
        // everything in the whole tree (the parent itself + every
        // subcategory), so nothing a customer already tagged directly to
        // the parent silently disappears.
        const treeIds = new Set([activeCatObj.id, ...subcats.map((s) => s.id)]);
        out = out.filter((p) => treeIds.has(p.category_id) || p.category === activeCatObj.slug);
      }
    } else if (activeCat !== "all") {
      // Legacy fallback: a `cat` slug that doesn't match any known category
      // (matches the pre-existing behavior for the free-text `category` field).
      out = out.filter((p) => p.category === activeCat);
    }
    if (activeBrand !== "all") {
      const b = brands.find((x) => x.slug === activeBrand);
      if (b) out = out.filter((p) => p.brand_id === b.id);
    }
    return out;
  }, [activeCat, activeCatObj, activeSubObj, subcats, activeBrand, products, brands]);

  const primary = tenant?.branding?.primary_color || "#3A86FF";
  const setCat = (c) => {
    if (c === "all") search.delete("cat"); else search.set("cat", c);
    search.delete("sub");  // switching category always clears any stale subcategory selection
    setSearch(search, { replace: true });
  };
  const setSub = (s) => {
    if (s === "all") search.delete("sub"); else search.set("sub", s);
    setSearch(search, { replace: true });
  };
  const setBrand = (b) => {
    if (b === "all") search.delete("brand"); else search.set("brand", b);
    setSearch(search, { replace: true });
  };
  const activeBrandObj = brands.find((x) => x.slug === activeBrand);

  return (
    <div className="page-enter" data-testid="products-page">
      {/* Persistent global header (address chip + search/bell/cart) */}
      <CustomerHeader primary={primary}/>
      <header className="sticky z-20 bg-brand-cream/95 backdrop-blur" style={{ top: "calc(68px + env(safe-area-inset-top, 0px))" }}>
        <div className="px-5 pt-3 pb-3 flex items-center gap-2">
          {/* No back button here on purpose — Shop is a primary bottom-tab
              destination (same tier as Home/Subs/Orders/Me), always reachable
              via the tab bar, so a dedicated back arrow is redundant UI even
              when arriving from a Home category/brand shortcut (still a tab
              switch, not a pushed sub-screen). */}
          <h1 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight flex-1 truncate">{t("shop_title")}</h1>
        </div>
        <div className="px-5 pb-4 flex gap-2 overflow-x-auto no-scrollbar">
          <CatChip slug="all" name={t("all")} active={activeCat === "all"} onClick={setCat} primary={primary}/>
          {topCats.map((c) => (
            <CatChip key={c.id} slug={c.slug} name={c.name} active={activeCat === c.slug} onClick={setCat} primary={primary}/>
          ))}
        </div>
        {/* Subcategory row — only appears when the selected top-level
            category actually has subcategories. Selecting the category
            itself shows this row plus every product in the whole tree by
            default ("All"); picking a specific chip narrows further. */}
        {subcats.length > 0 && (
          <div className="px-5 pb-4 flex gap-2 overflow-x-auto no-scrollbar" data-testid="subcategory-chips">
            <CatChip slug="all" name={`All ${activeCatObj.name}`} active={activeSub === "all"} onClick={setSub} primary={primary}/>
            {subcats.map((s) => (
              <CatChip key={s.id} slug={s.slug} name={s.name} active={activeSub === s.slug} onClick={setSub} primary={primary}/>
            ))}
          </div>
        )}
        {brands.length > 0 && (
          <div className="px-5 pb-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="brand-chips">
            <CatChip slug="all" name={t("all_brands")} active={activeBrand === "all"} onClick={setBrand} primary={primary}/>
            {brands.map((b) => (
              <CatChip key={b.id} slug={b.slug} name={b.name} active={activeBrand === b.slug} onClick={setBrand} primary={primary}/>
            ))}
          </div>
        )}
        {activeBrandObj && (
          <div className="px-5 pb-3 text-xs text-neutral-600 flex items-center gap-2" data-testid="active-brand-label">
            {t("showing_only")} <b>{activeBrandObj.name}</b>
            <button onClick={() => setBrand("all")} className="text-brand-sky font-bold underline" data-testid="clear-brand-filter">{t("clear")}</button>
          </div>
        )}
      </header>

      <main className={`px-5 ${count > 0 ? "pb-32" : ""}`}>
        {loading ? (
          <LoadingState testId="products-loading" />
        ) : loadError ? (
          <EmptyState
            icon={AlertTriangle}
            title="Couldn't load the shop"
            description={loadError}
            action={
              <button onClick={loadCatalogue}
                      className="h-10 px-4 rounded-2xl text-white text-sm font-bold"
                      style={{ background: primary }}
                      data-testid="products-retry">
                Retry
              </button>
            }
            testId="products-load-error"
          />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} primary={primary} className="w-full" />
            ))}
            {filtered.length === 0 && <div className="col-span-2 text-center text-neutral-500 py-10" data-testid="no-products">{t("no_products_category")}</div>}
          </div>
        )}
      </main>

      {/* CartRibbon is mounted globally in CustomerLayout — no per-page CTA */}
    </div>
  );
}

function CatChip({ slug, name, active, onClick, primary }) {
  return (
    <button onClick={() => onClick(slug)}
            className={`px-4 h-10 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${
              active ? "text-white" : "bg-white border border-neutral-200 text-neutral-700"
            }`}
            style={active ? { background: primary } : undefined}
            data-testid={`cat-chip-${slug}`}>
      {name}
    </button>
  );
}
